/**
 * Mock data simulating the Hotelpage (/search/hp/) response for hotel1
 * ("Hotel Eden Roma", hid: 1001001).
 *
 * The Hotelpage call extends SERP rates by adding book_hash to each rate,
 * and may return more rates / room types than the SERP.
 * The edge function returns { hotels: [{ id, hid, rates: RateHawkRate[] }] }.
 */

import type { RateHawkRate } from "@/lib/builder/ratehawk/types";

function makeRate(
  overrides: {
    match_hash: string;
    book_hash: string;
    meal: string;
    showAmount: string;
    currency: string;
    roomName: string;
    bedsLabel: string;
    bedType: string;
    amenities: string[];
    freeBefore: string | null;
    isPenalized?: boolean;
    nights?: number;
  },
): RateHawkRate {
  const {
    match_hash, book_hash, meal, showAmount, currency,
    roomName, bedsLabel, bedType, amenities, freeBefore,
    isPenalized = false, nights = 3,
  } = overrides;

  const perNight = (parseFloat(showAmount) / nights).toFixed(2);
  const chargeAmount = (parseFloat(showAmount) * 120).toFixed(2);

  const policies = isPenalized
    ? [
        {
          start_at: null,
          end_at: null,
          amount_show: showAmount,
          amount_charge: chargeAmount,
          commission_info: {
            show: { amount_gross: (parseFloat(showAmount) * 1.02).toFixed(2), amount_net: showAmount, amount_commission: (parseFloat(showAmount) * 0.02).toFixed(2) },
            charge: { amount_gross: (parseFloat(showAmount) * 122.4).toFixed(2), amount_net: chargeAmount, amount_commission: (parseFloat(showAmount) * 2.4).toFixed(2) },
          },
        },
      ]
    : [
        {
          start_at: null,
          end_at: freeBefore,
          amount_show: "0.00",
          amount_charge: "0.00",
          commission_info: {
            show: { amount_gross: "0.00", amount_net: "0.00", amount_commission: "0.00" },
            charge: { amount_gross: "0.00", amount_net: "0.00", amount_commission: "0.00" },
          },
        },
        {
          start_at: freeBefore,
          end_at: null,
          amount_show: showAmount,
          amount_charge: chargeAmount,
          commission_info: {
            show: { amount_gross: (parseFloat(showAmount) * 1.02).toFixed(2), amount_net: showAmount, amount_commission: (parseFloat(showAmount) * 0.02).toFixed(2) },
            charge: { amount_gross: (parseFloat(showAmount) * 122.4).toFixed(2), amount_net: chargeAmount, amount_commission: (parseFloat(showAmount) * 2.4).toFixed(2) },
          },
        },
      ];

  return {
    match_hash,
    search_hash: "sr-rh-search-001",
    book_hash,
    daily_prices: Array(nights).fill(perNight),
    meal,
    meal_data: { value: meal, has_breakfast: meal !== "nomeal", no_child_meal: false },
    payment_options: {
      payment_types: [
        {
          amount: chargeAmount,
          show_amount: showAmount,
          currency_code: "ILS",
          show_currency_code: currency,
          by: null,
          is_need_credit_card_data: false,
          is_need_cvc: false,
          type: "deposit",
          vat_data: { included: false, applied: false, amount: "0.00", currency_code: "USD", value: "0.00" },
          tax_data: { taxes: [] },
          perks: {},
          commission_info: {
            show: {
              amount_gross: (parseFloat(showAmount) * 1.02).toFixed(2),
              amount_net: showAmount,
              amount_commission: (parseFloat(showAmount) * 0.02).toFixed(2),
            },
            charge: {
              amount_gross: (parseFloat(showAmount) * 122.4).toFixed(2),
              amount_net: chargeAmount,
              amount_commission: (parseFloat(showAmount) * 2.4).toFixed(2),
            },
          },
          cancellation_penalties: {
            policies,
            free_cancellation_before: freeBefore,
          },
          recommended_price: null,
        },
      ],
    },
    bar_rate_price_data: null,
    rg_ext: { class: 3, quality: 5, sex: 0, bathroom: 2, bedding: 3, family: 0, capacity: 2, club: 0, bedrooms: 0, balcony: 0, view: 0, floor: 0 },
    room_name: roomName,
    room_name_info: { original_rate_name: roomName },
    serp_filters: ["has_bathroom"],
    sell_price_limits: null,
    allotment: 3,
    amenities_data: amenities,
    any_residency: false,
    deposit: null,
    no_show: null,
    room_data_trans: {
      main_room_type: roomName,
      main_name: roomName,
      bathroom: "private bathroom",
      bedding_type: bedsLabel,
      misc_room_type: null,
      beds: [{ bed: bedType, count: 1 }],
    },
    legal_info: null,
    is_package: false,
  };
}

export const RATEHAWK_RATES_MOCK: RateHawkRate[] = [
  // ── Classic Double Room ────────────────────────────────────────────────────
  makeRate({
    match_hash: "m-rh-001-std-brkf",
    book_hash: "h-rh-001-std-brkf-abc123",
    meal: "breakfast",
    showAmount: "420.00",
    currency: "EUR",
    roomName: "Classic Double Room",
    bedsLabel: "queen bed",
    bedType: "double",
    amenities: ["queen-bed", "non-smoking", "air-conditioning"],
    freeBefore: "2026-07-15T12:00:00",
  }),
  makeRate({
    match_hash: "m-rh-001-std-nomeal",
    book_hash: "h-rh-001-std-nomeal-def456",
    meal: "nomeal",
    showAmount: "370.00",
    currency: "EUR",
    roomName: "Classic Double Room",
    bedsLabel: "queen bed",
    bedType: "double",
    amenities: ["queen-bed", "non-smoking", "air-conditioning"],
    freeBefore: null,
    isPenalized: true,
  }),
  // ── Deluxe Suite with View ─────────────────────────────────────────────────
  makeRate({
    match_hash: "m-rh-001-suite-ai",
    book_hash: "h-rh-001-suite-ai-ghi789",
    meal: "all-inclusive",
    showAmount: "695.00",
    currency: "EUR",
    roomName: "Deluxe Suite with View",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "balcony", "panoramic-view", "mini-bar", "non-smoking", "air-conditioning"],
    freeBefore: "2026-07-10T12:00:00",
  }),
  makeRate({
    match_hash: "m-rh-001-suite-hb",
    book_hash: "h-rh-001-suite-hb-jkl012",
    meal: "halfboard",
    showAmount: "610.00",
    currency: "EUR",
    roomName: "Deluxe Suite with View",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "balcony", "panoramic-view", "mini-bar", "non-smoking"],
    freeBefore: "2026-07-10T12:00:00",
  }),
  makeRate({
    match_hash: "m-rh-001-suite-brkf",
    book_hash: "h-rh-001-suite-brkf-mno345",
    meal: "breakfast",
    showAmount: "555.00",
    currency: "EUR",
    roomName: "Deluxe Suite with View",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "balcony", "panoramic-view", "non-smoking"],
    freeBefore: null,
    isPenalized: true,
  }),
  // ── Junior Suite ────────────────────────────────────────────────────────────
  makeRate({
    match_hash: "m-rh-001-jsuite-brkf",
    book_hash: "h-rh-001-jsuite-brkf-pqr678",
    meal: "breakfast",
    showAmount: "510.00",
    currency: "EUR",
    roomName: "Junior Suite",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "sitting-area", "city-view", "non-smoking", "air-conditioning"],
    freeBefore: "2026-07-12T14:00:00",
  }),
  makeRate({
    match_hash: "m-rh-001-jsuite-nomeal",
    book_hash: "h-rh-001-jsuite-nomeal-stu901",
    meal: "nomeal",
    showAmount: "460.00",
    currency: "EUR",
    roomName: "Junior Suite",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "sitting-area", "city-view", "non-smoking"],
    freeBefore: "2026-07-12T14:00:00",
  }),
];

/**
 * Map from hid → rates, used by the mock hook to serve different data
 * depending on which hotel is selected.
 * For hotels 2 and 3 (hid 1001002, 1001003) we reuse a subset of
 * the above rates with renamed rooms.
 */
export const RATEHAWK_RATES_BY_HID: Record<number, RateHawkRate[]> = {
  1001001: RATEHAWK_RATES_MOCK,
  1001002: [
    makeRate({
      match_hash: "m-rh-002-std-hp",
      book_hash: "h-rh-002-std-hp-aaa",
      meal: "nomeal",
      showAmount: "185.00",
      currency: "EUR",
      roomName: "Standard Double Room",
      bedsLabel: "full double bed",
      bedType: "double",
      amenities: ["double-bed", "non-smoking", "desk"],
      freeBefore: "2026-07-18T14:00:00",
    }),
    makeRate({
      match_hash: "m-rh-002-brkf-hp",
      book_hash: "h-rh-002-brkf-hp-bbb",
      meal: "breakfast",
      showAmount: "215.00",
      currency: "EUR",
      roomName: "Standard Double Room",
      bedsLabel: "full double bed",
      bedType: "double",
      amenities: ["double-bed", "non-smoking", "desk"],
      freeBefore: "2026-07-18T14:00:00",
    }),
    makeRate({
      match_hash: "m-rh-002-sup-hp",
      book_hash: "h-rh-002-sup-hp-ccc",
      meal: "breakfast",
      showAmount: "260.00",
      currency: "EUR",
      roomName: "Superior Room with Terrace",
      bedsLabel: "king bed",
      bedType: "double",
      amenities: ["king-bed", "terrace", "city-view", "non-smoking"],
      freeBefore: null,
      isPenalized: true,
    }),
  ],
  1001003: [
    makeRate({
      match_hash: "m-rh-003-twin-hp",
      book_hash: "h-rh-003-twin-hp-ddd",
      meal: "nomeal",
      showAmount: "110.00",
      currency: "EUR",
      roomName: "Twin Room",
      bedsLabel: "2 single beds",
      bedType: "single",
      amenities: ["twin-beds", "shared-bathroom"],
      freeBefore: "2026-07-20T14:00:00",
    }),
    makeRate({
      match_hash: "m-rh-003-dbl-hp",
      book_hash: "h-rh-003-dbl-hp-eee",
      meal: "breakfast",
      showAmount: "130.00",
      currency: "EUR",
      roomName: "Double Room with Breakfast",
      bedsLabel: "double bed",
      bedType: "double",
      amenities: ["double-bed", "private-bathroom", "non-smoking"],
      freeBefore: null,
      isPenalized: true,
    }),
  ],
};
